import _ from "lodash";
/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import classes from "./ActivitySelector.scss";
import classNames from "classnames";
import ConditionalTooltip from "Post/components/ConditionalTooltip";
import {
  updateAllActivities,
  removeAllActivities,
  updateSingleActivity,
  removeSingleActivity,
  getIconUrl,
} from "Post/utils";
import ScrollableCarousel from "UIComponents/ScrollableCarousel";
import { isSafari } from "Utils";

const ListCriteriaValues = ({
  criteriaId,
  criteriaValues,
  onValueClick,
  isValueSelectedForCriteria,
  criteriaLabel,
}) => {
  return (
    <ScrollableCarousel scrollAmount={124 * 4}>
      <div className={classes.activitySelectorCardContainer}>
        {_.map(criteriaValues, selector => (
          <button
            key={selector?.id}
            onClick={() =>
              onValueClick(criteriaId, selector?.id, criteriaLabel)
            }
            className={classNames({
              [classes.activitySelectorCard]: true,
              [classes.selectedCriteriaValuesSelectorCard]: isValueSelectedForCriteria(
                criteriaId,
                _.get(selector, "id", "")
              ),
            })}
            data-test-id="journal-postcreation-activity-item"
          >
            {/* loading svg through img tag was rendering blurry in safari */}
            {isSafari() ? (
              <object
                type="image/svg+xml"
                data={getIconUrl(_.get(selector, "metadata.iconLink", ""))}
                className={classes.object}
              >
                {_.get(selector, "label", "")}
              </object>
            ) : (
              <img
                src={getIconUrl(_.get(selector, "metadata.iconLink", ""))}
                alt={_.get(selector, "label", "")}
                className={classes.object}
              />
            )}
            <ConditionalTooltip tooltip={_.get(selector, "label", "")}>
              <span
                className={classNames({
                  [classes.activitySelectorCardText]: true,
                  [classes.selectedCriteriaValuesSelectorCardText]: isValueSelectedForCriteria(
                    criteriaId,
                    _.get(selector, "id", "")
                  ),
                })}
              >
                {_.get(selector, "label", "")}
              </span>
            </ConditionalTooltip>
          </button>
        ))}
      </div>
    </ScrollableCarousel>
  );
};

/*
  Criteria indicates the field on which some specific value will be selected. (i.e. For food activity criterias will be Meal type, quantity)
  Value is contained in criteria. one criteria may have multiple values. (i.e. for Meal type values are -> Breakfast, lunch, etc.)
*/
const ActivitySelector = ({
  activityCriteriaSets,
  selectedStudentForActivity,
  studentActivityData,
  updatePost,
  studentIds,
  setShowOverrideModal,
  setToastMsg,
  activityName,
  resetSelectStudentDataError,
}) => {
  const isValueSelectedForCriteria = (criteriaId, valueId) => {
    const currentCriteria = _.get(studentActivityData, [
      selectedStudentForActivity,
      "criteria",
      criteriaId,
    ]);
    return (
      !_.isNil(currentCriteria) && _.get(currentCriteria, "value") == valueId
    );
  };

  const onValueClick = (criteriaId, valueId, label) => {
    resetSelectStudentDataError("data"); // reset error if any

    if (!isValueSelectedForCriteria(criteriaId, valueId)) {
      // add value
      if (selectedStudentForActivity === "ALL") {
        const result = updateAllActivities({
          studentIds,
          currentStudentActivities: studentActivityData,
          key: ["criteria", criteriaId],
          value: { value: valueId },
        });
        if (_.get(result, "shouldShowOverrideModal", false)) {
          setShowOverrideModal?.({
            property: label,
            operation: "UPDATE",
            updatedActivities: _.get(result, "updatedStudentActivities", {}),
          });
        } else {
          updatePost({
            studentActivityData: _.get(result, "updatedStudentActivities", {}),
          });
        }
        return;
      }

      // Update specific student activity details
      const updatedStudentActivities = updateSingleActivity({
        studentIds,
        studentId: selectedStudentForActivity,
        currentStudentActivities: studentActivityData,
        key: ["criteria", criteriaId],
        value: { value: valueId },
      });
      if (
        _.get(updatedStudentActivities, [
          selectedStudentForActivity,
          "criteria",
          criteriaId,
          "status",
        ]) != "ALL" &&
        _.size(studentIds) > 1
      )
        setToastMsg({
          msg: "toastMsgs:details_updated_with_label",
          locale_params: [
            {
              key: "label",
              value: activityName,
              isPlainText: false,
            },
          ],
          type: "info",
        });
      updatePost({ studentActivityData: updatedStudentActivities });
      return;
    }

    if (selectedStudentForActivity === "ALL") {
      const updatedStudentActivities = removeAllActivities({
        studentIds,
        currentStudentActivities: studentActivityData,
        key: ["criteria", criteriaId],
        value: { value: valueId },
      });
      updatePost({
        studentActivityData: updatedStudentActivities,
      });
      return;
    }

    // remove selected value
    const updatedStudentActivities = removeSingleActivity({
      studentIds,
      studentId: selectedStudentForActivity,
      currentStudentActivities: studentActivityData,
      key: ["criteria", criteriaId],
      value: { value: valueId },
    });
    if (_.size(studentIds) > 1)
      setToastMsg({
        msg: "toastMsgs:details_updated_with_label",
        locale_params: [
          {
            key: "label",
            value: activityName,
            isPlainText: false,
          },
        ],
        type: "info",
      });
    updatePost({ studentActivityData: updatedStudentActivities });
  };

  if (_.isNil(activityCriteriaSets)) return null;

  return (
    <div className={classes.activitySelectorContainer}>
      {_.map(activityCriteriaSets, criteria => (
        <div key={_.get(criteria, "id")}>
          <span className={classes.activitySelectorTitle}>
            {_.get(criteria, "label", "")}
          </span>
          <ListCriteriaValues
            onValueClick={onValueClick}
            isValueSelectedForCriteria={isValueSelectedForCriteria}
            criteriaId={_.get(criteria, "id")}
            criteriaLabel={_.get(criteria, "label")}
            criteriaValues={_.get(criteria, "academicCriteriaValues", [])}
          />
        </div>
      ))}
    </div>
  );
};

export default ActivitySelector;
